import {
  memo,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Tooltip from '../Tooltip';
import { ISelect, ISelectOption } from '../../../models';
import './styles.scss';

/* ============================
   Value with truncation tooltip
   ============================ */

const ValueWithTooltip = ({ text }: { text: string }) => {
  const valueRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = () => {
    const el = valueRef.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth);
  };

  useEffect(() => {
    checkTruncation();
    if (!valueRef.current) return;
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(valueRef.current);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Tooltip
      title={isTruncated ? text : ''}
      placement="bottom"
      disableHoverListener={!isTruncated}
    >
      <div ref={valueRef} className="ellipsis-text custom-select__single-value">
        {text}
      </div>
    </Tooltip>
  );
};

type FormattedOption = { label: string; value: string; _value: string };

/* ============================
   CustomSelect (react-select stand-in, no external dependency)
   ============================ */

const CustomSelect = forwardRef<any, ISelect>((props, ref) => {
  const {
    className = '',
    hide_arrow = false,
    options = [],
    value = '',
    onChange,
    name = 'select_name',
    onScroll,
    onInputChange,
    border_bottom = true,
    small,
    noneOption,
    allOption,
    width = '100%',
    getLabelAndValue,
    selectMenuWithOnlyOneOption = false,
    disabled = false,
    fieldType = '',
    isSearchable = false,
    required = false,
    asyncLoadOptions,
    asyncLoadOptionById,
    pageSize: pageSizeProp,
  } = props;

  const [selectValue, setSelectValue] = useState(value?.toString() || '');
  const [asyncOptions, setAsyncOptions] = useState<ISelectOption[]>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isLoadingValueOption, setIsLoadingValueOption] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  const hasAsyncPagination = !!asyncLoadOptions;
  const hasAsyncValueLoader = !!asyncLoadOptionById;
  const isAsync = hasAsyncPagination || hasAsyncValueLoader;
  const pageSize = pageSizeProp || 100;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus() ?? controlRef.current?.focus(),
  }));

  /* ---------- sync external value ---------- */

  useEffect(() => {
    setSelectValue(value?.toString() || '');
  }, [value]);

  const hasError = required && !selectValue;

  /* ---------- async loading (pagination) ---------- */

  const loadPage = useCallback(
    async (nextPage: number, searchTerm: string, reset = false) => {
      if (!asyncLoadOptions || isLoadingPage) return;
      try {
        setIsLoadingPage(true);
        const result = await asyncLoadOptions({
          page: nextPage,
          pageSize,
          search: searchTerm ? searchTerm.toLowerCase() : undefined,
        });
        const newOptions = Array.isArray(result) ? result : [];
        setAsyncOptions((prev) => (reset ? newOptions : [...prev, ...newOptions]));
        setPage(nextPage);
        setHasMore(newOptions.length === pageSize);
      } finally {
        setIsLoadingPage(false);
      }
    },
    [asyncLoadOptions, isLoadingPage, pageSize],
  );

  useEffect(() => {
    if (!hasAsyncPagination || !isOpen) return;
    setAsyncOptions([]);
    setPage(1);
    setHasMore(true);
    loadPage(1, '', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAsyncPagination, isOpen]);

  /* ---------- option formatter ---------- */

  const formatOption = useCallback(
    (option: number | string | ISelectOption): FormattedOption => {
      if (!option) return { label: '', value: '', _value: '' };

      if (typeof option === 'object') {
        if (getLabelAndValue) {
          const { label, value: v } = getLabelAndValue(option);
          return {
            label,
            value: String(v),
            _value: String(
              option.value && option.value !== v ? option.value : label,
            ),
          };
        }
        const label =
          option.label ||
          option.name ||
          option.spn ||
          option.series ||
          option.value ||
          option.model ||
          '';
        const v = option.id ?? option.value ?? '';
        return {
          label,
          value: String(v),
          _value: String(option.value && option.value !== v ? option.value : label),
        };
      }

      const str = String(option);
      return { label: str, value: str, _value: str };
    },
    [getLabelAndValue],
  );

  const formattedOptions = useMemo(() => {
    const baseOptions = Array.isArray(options) ? options : [];
    const sourceOptions =
      hasAsyncPagination || hasAsyncValueLoader
        ? [...baseOptions, ...asyncOptions]
        : baseOptions;
    const seen = new Set<string>();
    return sourceOptions.map(formatOption).filter((opt) => {
      if (seen.has(opt.value)) return false;
      seen.add(opt.value);
      return true;
    });
  }, [options, asyncOptions, hasAsyncPagination, hasAsyncValueLoader, formatOption]);

  /* ---------- ensure selected value option exists (async) ---------- */

  useEffect(() => {
    if (!hasAsyncValueLoader || !asyncLoadOptionById) return;
    if (!selectValue) return;
    const exists = formattedOptions.some((opt) => opt.value === selectValue);
    if (exists) return;

    let cancelled = false;
    const fetchOption = async () => {
      try {
        setIsLoadingValueOption(true);
        const option = await asyncLoadOptionById(selectValue);
        if (!option || cancelled) return;
        setAsyncOptions((prev) => [...prev, option]);
      } finally {
        if (!cancelled) setIsLoadingValueOption(false);
      }
    };
    fetchOption();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAsyncValueLoader, asyncLoadOptionById, selectValue, formattedOptions]);

  /* ---------- auto-select single option ---------- */

  useEffect(() => {
    if (selectMenuWithOnlyOneOption && formattedOptions.length === 1 && !selectValue) {
      const opt = formattedOptions[0];
      setSelectValue(opt.value);
      onChange?.(opt.value, name, fieldType, opt.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectMenuWithOnlyOneOption, formattedOptions, selectValue]);

  /* ---------- displayed options (+ noneOption/allOption + local search) ---------- */

  const displayedOptions = useMemo(() => {
    const list = [
      ...(noneOption ? [{ label: '--', value: '', _value: '' }] : []),
      ...(allOption ? [{ label: 'All', value: '', _value: '' }] : []),
      ...formattedOptions,
    ];
    if (isSearchable && search && !hasAsyncPagination) {
      const needle = search.toLowerCase();
      return list.filter((o) => o.label.toLowerCase().includes(needle));
    }
    return list;
  }, [formattedOptions, noneOption, allOption, isSearchable, search, hasAsyncPagination]);

  const selectedOpt = useMemo(
    () => displayedOptions.find((opt) => opt.value === selectValue) || null,
    [displayedOptions, selectValue],
  );

  /* ---------- positioning ---------- */

  const updateMenuPosition = useCallback(() => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuStyle({ top: rect.bottom, left: rect.left, width: rect.width });
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    updateMenuPosition();
    setIsOpen(true);
    setSearch('');
    setHighlighted(Math.max(displayedOptions.findIndex((o) => o.value === selectValue), 0));
    if (isSearchable) setTimeout(() => inputRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, updateMenuPosition, isSearchable]);

  const closeMenu = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (
        wrapperRef.current?.contains(e.target as Node) ||
        menuRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      closeMenu();
    };
    const onScrollOrResize = () => updateMenuPosition();
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, closeMenu, updateMenuPosition]);

  /* ---------- handlers ---------- */

  const handleSelectOption = useCallback(
    (opt: FormattedOption) => {
      if (opt.value !== selectValue) {
        setSelectValue(opt.value);
        onChange?.(opt.value || null, name, fieldType, opt.label, opt as unknown as ISelectOption);
      }
      closeMenu();
    },
    [selectValue, onChange, name, fieldType, closeMenu],
  );

  const handleInputChange = useCallback(
    (val: string) => {
      setSearch(val);
      onInputChange?.(val, { action: 'input-change' });
      if (hasAsyncPagination) {
        setAsyncOptions([]);
        setPage(1);
        setHasMore(true);
        loadPage(1, val, true);
      }
    },
    [onInputChange, hasAsyncPagination, loadPage],
  );

  const handleMenuScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) {
        if (hasAsyncPagination && hasMore && !isLoadingPage) {
          loadPage(page + 1, search, false);
        }
        onScroll?.(e.nativeEvent as unknown as WheelEvent);
      }
    },
    [hasAsyncPagination, hasMore, isLoadingPage, loadPage, page, search, onScroll],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          e.preventDefault();
          openMenu();
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((h) => Math.min(h + 1, displayedOptions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const opt = displayedOptions[highlighted];
        if (opt) handleSelectOption(opt);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
      } else if (e.key === 'Tab') {
        closeMenu();
      }
    },
    [isOpen, openMenu, displayedOptions, highlighted, handleSelectOption, closeMenu],
  );

  const selectedExtraValue = useMemo(() => {
    const opt = formattedOptions.find((o) => o.value === selectValue);
    return opt?._value || opt?.label || '';
  }, [formattedOptions, selectValue]);

  return (
    <div
      ref={wrapperRef}
      data-select-name={name}
      data-select-extra-value={selectedExtraValue}
      className={`custom-select-wrapper ${className} ${disabled ? 'non-clickable' : ''} ${
        hasError ? 'has-error' : ''
      }`}
      style={{ width: small ? '70px' : width }}
    >
      <div
        ref={controlRef}
        className={`custom-select__control ${border_bottom ? '' : 'no-border-bottom'} ${
          isOpen ? 'is-focused' : ''
        } ${disabled ? 'is-disabled' : ''}`}
        tabIndex={disabled ? -1 : 0}
        onMouseDown={(e) => {
          if (isSearchable) return; // let the input own focus/open
          e.preventDefault();
          isOpen ? closeMenu() : openMenu();
        }}
        onKeyDown={handleKeyDown}
      >
        {isSearchable ? (
          <input
            ref={inputRef}
            className="custom-select__input"
            value={isOpen ? search : selectedOpt?.label || ''}
            placeholder="__"
            disabled={disabled}
            onFocus={openMenu}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        ) : (
          // No onClick here: the control's onMouseDown above already toggles the
          // menu, and mousedown on this child bubbles up to it. A second
          // handler on the click that follows would re-toggle and slam the
          // menu shut in the same gesture that opened it.
          <div className="custom-select__value">
            {selectedOpt ? (
              <ValueWithTooltip text={selectedOpt.label} />
            ) : (
              <span className="custom-select__placeholder">__</span>
            )}
          </div>
        )}
        {!hide_arrow && <span className="custom-select__indicator" />}
        {(isLoadingValueOption || (isAsync && isLoadingPage && !isOpen)) && (
          <span className="custom-select__loading" />
        )}
      </div>

      {isOpen && menuStyle && (
        <div
          ref={menuRef}
          className="custom-select__menu"
          style={{ top: menuStyle.top, left: menuStyle.left, width: menuStyle.width }}
          onScroll={handleMenuScroll}
        >
          {displayedOptions.length === 0 && (
            <div className="custom-select__no-options">
              {isLoadingPage ? 'Loading…' : 'No options'}
            </div>
          )}
          {displayedOptions.map((opt, idx) => (
            <div
              key={`${opt.value}-${idx}`}
              className={`custom-select__option ${opt.value === selectValue ? 'is-selected' : ''} ${
                idx === highlighted ? 'is-focused' : ''
              }`}
              onMouseEnter={() => setHighlighted(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectOption(opt);
              }}
            >
              {opt.label}
            </div>
          ))}
          {isLoadingPage && displayedOptions.length > 0 && (
            <div className="custom-select__no-options">Loading…</div>
          )}
        </div>
      )}
    </div>
  );
});

export default memo(CustomSelect);
